import React, {useRef, useState} from 'react';

import {Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {FrontEvent} from '../../../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {UnreachableError} from '../../../../../../libs/shared/core/src/helpers/types/customErrors';
import {onlyItem} from '../../../../../../libs/shared/core/src/helpers/utilities/arrayHelpers';
import {TeamModel} from '../../../../../../libs/shared/core/src/models/api/teamModel';
import {InboxesPanelId} from '../../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {DropdownProps} from '../../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {DropdownSeparator} from '../../../../../../libs/shared/ui/src/components/dropdowns/dropdownSeparator';
import {SimpleDropdown} from '../../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown/simpleDropdown';
import {
  LinkNavigator,
  useLinkNavigation,
} from '../../../../../../libs/shared/ui/src/components/navigation/useLinkNavigation';
import {Anchor} from '../../../../../../libs/shared/ui/src/components/reposition/repositionHelpers';
import {useAppSelector} from '../../../../../helpers/appReduxHelpers';
import {SettingsResourcePagesEnum, SettingsRootPagesEnum} from '../../../../../models/url/models/types';
import {buildSettingsPath} from '../../../../../models/url/settingsUrlHelpers';
import {getTeamsUserIsAdminOf} from '../../../../../store/data/teams/teamsSelectors';
import {PermissionSimpleDropdownItem} from '../../../../common/dropdowns/permissionSimpleDropdownItem';
import {SearchNamespacesDropdown} from '../../../../common/dropdowns/search/searchNamespacesDropdown';
import {useOnEditWorkspace} from '../../header/workspaceHeaderHelpers';
import {
  renderWorkspaceHeaderMoreDefaultDropdownItems,
  WorkspaceHeaderMoreDropdownRenderProps,
} from '../../header/workspaceHeaderMore';
import {WorkspaceLineMoreButton} from '../../line/workspaceLineMoreButton';

/*
 * Types.
 */

interface LensesWorkspaceHeaderMoreProps
  extends Omit<WorkspaceHeaderMoreDropdownRenderProps, 'onEditWorkspace'>,
    Pick<DropdownProps, 'placement'> {
  panelId: InboxesPanelId;
  showCreateLensDialog: () => void;
  renderButton?: (props: {
    isOpen: boolean;
    buttonRef: React.Ref<HTMLDivElement>;
    onClick: () => void;
  }) => React.ReactNode;
  shouldHideDefaultWorkspaceActions?: boolean;
}

interface LensesWorkspaceHeaderMoreDropdownRenderProps extends LensesWorkspaceHeaderMoreProps {
  anchor: Anchor;
  onRequestClose: () => void;
  setActiveDropdown: (dropdown: LensesWorkspaceDropdownsEnum | null) => void;
  teamsUserIsAdminOf: Array<TeamModel>;
}

enum LensesWorkspaceDropdownsEnum {
  DEFAULT = 'DEFAULT',
  SELECT_WORKSPACE = 'SELECT_WORKSPACE',
}

/*
 * Component.
 */

export const LensesWorkspaceHeaderMore = (props: LensesWorkspaceHeaderMoreProps) => {
  const buttonRef = useRef<HTMLDivElement>(null);

  const teamsUserIsAdminOf = useAppSelector(getTeamsUserIsAdminOf);

  const [activeDropdown, setActiveDropdown] = useState<LensesWorkspaceDropdownsEnum | null>(null);

  /*
   * Event Handlers.
   */

  const onRequestClose = () => {
    setActiveDropdown(null);
  };

  const onClick = () => setActiveDropdown(LensesWorkspaceDropdownsEnum.DEFAULT);

  /*
   * Render.
   */

  const renderDropdown = () => {
    if (!activeDropdown) {
      return null;
    }

    switch (activeDropdown) {
      case LensesWorkspaceDropdownsEnum.DEFAULT:
        return (
          <DefaultDropdown
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...props}
            anchor={buttonRef}
            setActiveDropdown={setActiveDropdown}
            onRequestClose={onRequestClose}
            teamsUserIsAdminOf={teamsUserIsAdminOf}
          />
        );
      case LensesWorkspaceDropdownsEnum.SELECT_WORKSPACE:
        return (
          <SelectWorkspaceDropdown
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...props}
            anchor={buttonRef}
            setActiveDropdown={setActiveDropdown}
            onRequestClose={onRequestClose}
            teamsUserIsAdminOf={teamsUserIsAdminOf}
          />
        );

      default:
        throw new UnreachableError(activeDropdown);
    }
  };

  const isDropdownOpen = Boolean(activeDropdown);

  return (
    <>
      {props.renderButton?.({buttonRef, isOpen: isDropdownOpen, onClick}) ?? (
        <WorkspaceLineMoreButton ref={buttonRef} isMoreOpen={isDropdownOpen} onClick={onClick} />
      )}
      {renderDropdown()}
    </>
  );
};

const DefaultDropdown = (props: LensesWorkspaceHeaderMoreDropdownRenderProps) => {
  const onEditWorkspace = useOnEditWorkspace(props);
  const {navigateToLink} = useLinkNavigation();

  const onCreateSharedViewClick = (event: FrontEvent) => {
    event.preventDefault();

    const onlyAdminTeam = onlyItem(props.teamsUserIsAdminOf);

    // Skip workspace selection if there is only 1 option
    if (onlyAdminTeam) {
      navigateToCreateSharedView(navigateToLink, onlyAdminTeam.namespace);
      return;
    }

    props.setActiveDropdown(LensesWorkspaceDropdownsEnum.SELECT_WORKSPACE);
  };

  return (
    <SimpleDropdown anchor={props.anchor} onRequestClose={props.onRequestClose} placement={props.placement}>
      <PermissionSimpleDropdownItem onSelect={props.showCreateLensDialog}>
        <Trans id="lenses-workspace-create-lens">Create private view</Trans>
      </PermissionSimpleDropdownItem>
      {props.teamsUserIsAdminOf.length > 0 && (
        <PermissionSimpleDropdownItem onSelect={onCreateSharedViewClick}>
          <Trans id="lenses-workspace-create-lens">Create shared view</Trans>
        </PermissionSimpleDropdownItem>
      )}
      {!props.shouldHideDefaultWorkspaceActions && (
        <>
          <DropdownSeparator />
          {renderWorkspaceHeaderMoreDefaultDropdownItems({...props, onEditWorkspace})}
        </>
      )}
    </SimpleDropdown>
  );
};

const SelectWorkspaceDropdown = (props: LensesWorkspaceHeaderMoreDropdownRenderProps) => {
  const {navigateToLink} = useLinkNavigation();

  const onNamespaceSelect = (namespace: string) => {
    navigateToCreateSharedView(navigateToLink, namespace);
  };

  return (
    <SearchNamespacesDropdown
      anchor={props.anchor}
      placement={props.placement}
      onRequestClose={props.onRequestClose}
      selectedNamespace={undefined}
      teams={props.teamsUserIsAdminOf}
      onChange={onNamespaceSelect}
      headerTitle={<Trans id="lenses-workspace-select-workspace">Select workspace</Trans>}
      onHeaderBackButtonClick={() => props.setActiveDropdown(LensesWorkspaceDropdownsEnum.DEFAULT)}
      headerBackButtonLabel={<Trans id="lenses-workspace-actions-menu-back">Back to actions</Trans>}
    />
  );
};

/*
 * Helpers.
 */

function navigateToCreateSharedView(navigateToLink: LinkNavigator, namespace: string) {
  return navigateToLink(
    buildSettingsPath({
      namespace,
      rootPage: SettingsRootPagesEnum.LENSES,
      resourcePage: SettingsResourcePagesEnum.NEW,
      lensId: undefined,
      tagIdsFilter: undefined,
    }),
  );
}
